/**
 * Country list derived from the ITU-T E.164 metadata bundled inside
 * libphonenumber-js (Google's official phone-number library port).
 *
 * Single source of truth across the wizard:
 *   - Nationality + Issuing-country dropdowns (RegisterScreen / CompanionsScreen)
 *   - Dial-code picker in PhoneInput
 *
 * Region grouping (used by CountryPicker's tab strip):
 *   - europe_and_nearby (default): EU + EFTA + UK + Balkans + Eastern Europe
 *     + Caucasus + Turkey + Israel + Levant. Matches the guest mix a Greek
 *     property realistically sees walking in the door.
 *   - middle_east / africa / asia / americas / oceania: standard UN M49
 *     assignments (Egypt stays in Africa, Turkey/Israel in europe_and_nearby).
 *
 * Names come from `Intl.DisplayNames('en', { type: 'region' })` so we don't
 * hand-maintain another translation table. Greenland's geopolitical name vs
 * Stripe vs ISO mismatches are not a concern at this scale.
 */

import { getCountries, getCountryCallingCode } from "libphonenumber-js/min";

export type CountryRegion =
  | "europe_and_nearby"
  | "middle_east"
  | "africa"
  | "asia"
  | "americas"
  | "oceania";

export interface Country {
  iso2: string;
  name: string;
  dialCode: string;
  /** 🇬🇷 etc. — derived from iso2 via regional-indicator code points. */
  flag: string;
  region: CountryRegion;
}

const NAMES = new Intl.DisplayNames(["en"], { type: "region" });

function isoToFlag(iso2: string): string {
  return iso2
    .toUpperCase()
    .replace(/./g, (ch) => String.fromCodePoint(0x1f1e6 + ch.charCodeAt(0) - 65));
}

// ── Region assignments ──────────────────────────────────────────────────────
// The "Europe & nearby" bucket is intentionally broad: everything a Greek
// hotel realistically sees at the front desk without paging through a global
// list. Turkey, Israel and Russia sit in this bucket, not Middle East / Asia,
// per product spec.

const EUROPE_AND_NEARBY = new Set<string>([
  // EU-27
  "AT","BE","BG","HR","CY","CZ","DK","EE","FI","FR","DE","GR","HU","IE","IT",
  "LV","LT","LU","MT","NL","PL","PT","RO","SK","SI","ES","SE",
  // EFTA + micro-states + UK
  "IS","LI","NO","CH","GB","MC","SM","VA","AD","GI","JE","GG","IM","FO","AX",
  // Balkans
  "AL","BA","XK","ME","MK","RS",
  // Eastern Europe (incl. Russia per spec)
  "UA","BY","MD","RU",
  // Caucasus
  "AM","AZ","GE",
  // Near-East neighbours (Turkey, Levant, Israel per spec)
  "TR","IL","LB","SY","JO","PS",
]);

const MIDDLE_EAST = new Set<string>([
  // Arab Peninsula + Iran/Iraq (Levant + Turkey/Israel live in europe_and_nearby)
  "SA","AE","QA","KW","BH","OM","YE","IR","IQ",
]);

const AFRICA = new Set<string>([
  "DZ","AO","BJ","BW","BF","BI","CM","CV","CF","TD","KM","CG","CD","DJ","EG",
  "GQ","ER","SZ","ET","GA","GM","GH","GN","GW","CI","KE","LS","LR","LY","MG",
  "MW","ML","MR","MU","YT","MA","MZ","NA","NE","NG","RE","RW","SH","ST","SN",
  "SC","SL","SO","ZA","SS","SD","TZ","TG","TN","UG","EH","ZM","ZW",
]);

const ASIA = new Set<string>([
  "AF","BD","BT","BN","KH","CN","HK","IN","ID","JP","KZ","KG","LA","MO","MY",
  "MV","MN","MM","NP","KP","PK","PH","SG","KR","LK","TW","TJ","TH","TL","TM",
  "UZ","VN",
]);

const AMERICAS = new Set<string>([
  "AI","AG","AR","AW","BS","BB","BZ","BM","BO","BQ","BR","VG","CA","KY","CL",
  "CO","CR","CU","CW","DM","DO","EC","SV","FK","GF","GL","GD","GP","GT","GY",
  "HT","HN","JM","MQ","MX","MS","NI","PA","PY","PE","PR","BL","KN","LC","MF",
  "PM","VC","SX","SR","TT","TC","US","UY","VE","VI",
]);

const OCEANIA = new Set<string>([
  "AS","AU","CK","FJ","PF","GU","KI","MH","FM","NR","NC","NZ","NU","NF","MP",
  "PW","PG","PN","WS","SB","TK","TO","TV","UM","VU","WF",
]);

function regionOf(iso2: string): CountryRegion {
  if (EUROPE_AND_NEARBY.has(iso2)) return "europe_and_nearby";
  if (MIDDLE_EAST.has(iso2))       return "middle_east";
  if (AFRICA.has(iso2))            return "africa";
  if (ASIA.has(iso2))              return "asia";
  if (AMERICAS.has(iso2))          return "americas";
  if (OCEANIA.has(iso2))           return "oceania";
  // Fallthrough for exotic ISO codes libphonenumber knows but the sets miss —
  // Antarctica, Bouvet, etc. Bucket them into "asia" since they're globally
  // rare and this keeps the picker from silently hiding them.
  return "asia";
}

// ── Region tab strip metadata ───────────────────────────────────────────────
// Order = default UI order in the picker. `europe_and_nearby` first because
// it's the default region for Greek properties.
export const REGIONS: { id: CountryRegion; label: string; short: string }[] = [
  { id: "europe_and_nearby", label: "Europe & nearby", short: "Europe" },
  { id: "middle_east",       label: "Middle East",     short: "Middle East" },
  { id: "africa",            label: "Africa",          short: "Africa" },
  { id: "asia",              label: "Asia",            short: "Asia" },
  { id: "americas",          label: "Americas",        short: "Americas" },
  { id: "oceania",           label: "Oceania",         short: "Oceania" },
];

function buildList(): Country[] {
  const list: Country[] = [];
  for (const iso2 of getCountries()) {
    // getCountryCallingCode throws for an unknown region — skip defensively.
    let dial: string;
    try { dial = `+${getCountryCallingCode(iso2)}`; }
    catch { continue; }
    list.push({
      iso2,
      name: NAMES.of(iso2) ?? iso2,
      dialCode: dial,
      flag: isoToFlag(iso2),
      region: regionOf(iso2),
    });
  }
  list.sort((a, b) => a.name.localeCompare(b.name, "en"));
  return list;
}

export const COUNTRIES: Country[] = buildList();

export function countryName(iso2: string | null | undefined): string {
  if (!iso2) return "";
  return COUNTRIES.find((c) => c.iso2 === iso2)?.name ?? iso2;
}

export function countriesInRegion(region: CountryRegion): Country[] {
  return COUNTRIES.filter((c) => c.region === region);
}

/** Used by PhoneInput when parsing a stored E.164 string back into its parts. */
export const COUNTRIES_BY_DIAL_LENGTH: Country[] = [...COUNTRIES].sort(
  (a, b) => b.dialCode.length - a.dialCode.length
);
